// Lens / view / focus prefs are still per-tab (sessionStorage). Only the
// PiP geometry (size + position) was promoted to per-user persistence; see
// the preview-prefs cache below.
const MAIN_LENS_KEY = "omni_watch_preview_main_lens";
const VIEW_360_KEY = "omni_watch_preview_360_view";
/** Flashback history/playback panel — defaults to immersive 360°. */
const FLASHBACK_VIEW_360_KEY = "flashback_playback_360_view";
const FOCUS_KEY = "omni_watch_focus_ring";

/**
 * localStorage namespace for the per-user preview preferences cache.
 * Optimistic local copy of what the server has — written immediately on user
 * input for instant first paint, server PUT is debounced. Keyed by user id
 * so two users sharing the same browser do not see each other's geometry.
 */
const PREVIEW_PREFS_CACHE_PREFIX = "omni_watch:prefs:preview:";

export type MainLens = "360" | "ptz";
export type View360Mode = "flat" | "immersive";

export type FocusRingPersist = {
  normX: number;
  normY: number;
  zoom: number;
};

/** @deprecated Use FocusRingPersist — kept for existing imports. */
export type FocusRing = FocusRingPersist;

/**
 * Floating (small) preview share of the stage, expressed as a percent of the
 * stage's *width and height*. Default 10 → 90/10 main vs floating allocation.
 *
 * Stored authoritatively on the server (per user). The client also keeps a
 * `localStorage` cache for instant paint while the server hydration is
 * in-flight.
 */
export const PIP_SHARE_DEFAULT = 10;
export const PIP_SHARE_MIN = 10;
export const PIP_SHARE_MAX = 90;
/** Background PiP edge arrows show when small preview share is at or above this %. */
export const PIP_SHARE_SHOW_PTZ_EDGE_ARROWS_AT = 20;

/**
 * Floating preview position as fractions of the stage rect (0..1).
 * Persisting in fractions (not pixels) keeps the relative position stable
 * when the stage resizes — e.g. fullscreen toggle or window resize.
 */
export type PipPositionPct = { leftPct: number; topPct: number };

export type PreviewPrefs = {
  pipSharePct: number;
  pipPosition: PipPositionPct;
};

export const DEFAULT_PREVIEW_PREFS: PreviewPrefs = {
  pipSharePct: PIP_SHARE_DEFAULT,
  pipPosition: { leftPct: 0.02, topPct: 0.04 },
};

function safeGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

export function getMainLens(): MainLens {
  return safeGet(MAIN_LENS_KEY) === "ptz" ? "ptz" : "360";
}

export function setMainLens(v: MainLens): void {
  safeSet(MAIN_LENS_KEY, v);
}

export const persistMainLens = setMainLens;

export function getView360Mode(): View360Mode {
  return safeGet(VIEW_360_KEY) === "immersive" ? "immersive" : "flat";
}

export function setView360Mode(v: View360Mode): void {
  safeSet(VIEW_360_KEY, v);
}

export const persistView360Mode = setView360Mode;

export function getFlashbackView360Mode(): View360Mode {
  const v = safeGet(FLASHBACK_VIEW_360_KEY);
  if (v === "immersive" || v === "flat") return v;
  return "immersive";
}

export function setFlashbackView360Mode(v: View360Mode): void {
  safeSet(FLASHBACK_VIEW_360_KEY, v);
}

export const persistFlashbackView360Mode = setFlashbackView360Mode;

/** When both streams exist, hide the draggable small-preview (PiP) overlay. */
const PIP_POPUP_HIDDEN_KEY = "omni_watch_preview_pip_popup_hidden";

export function getPipPopupHidden(): boolean {
  return safeGet(PIP_POPUP_HIDDEN_KEY) === "1";
}

export function setPipPopupHidden(hidden: boolean): void {
  safeSet(PIP_POPUP_HIDDEN_KEY, hidden ? "1" : "0");
}

const defaultFocus = (): FocusRingPersist => ({
  normX: 0.5,
  normY: 0.82,
  zoom: 5,
});

export function getFocusRing(): FocusRingPersist {
  const raw = safeGet(FOCUS_KEY);
  if (!raw) return defaultFocus();
  try {
    const o = JSON.parse(raw) as Partial<FocusRingPersist>;
    if (
      typeof o.normX === "number" &&
      typeof o.normY === "number" &&
      typeof o.zoom === "number" &&
      Number.isFinite(o.normX) &&
      Number.isFinite(o.normY) &&
      Number.isFinite(o.zoom)
    ) {
      return {
        normX: Math.min(1, Math.max(0, o.normX)),
        normY: Math.min(1, Math.max(0, o.normY)),
        zoom: Math.min(25, Math.max(1, Math.round(o.zoom))),
      };
    }
  } catch {
    /* ignore */
  }
  return defaultFocus();
}

export function setFocusRing(v: FocusRingPersist): void {
  safeSet(
    FOCUS_KEY,
    JSON.stringify({
      normX: Math.min(1, Math.max(0, v.normX)),
      normY: Math.min(1, Math.max(0, v.normY)),
      zoom: Math.min(25, Math.max(1, Math.round(v.zoom))),
    }),
  );
}

export const persistFocus = setFocusRing;

// ──────────────────────────────────────────────────────────────────────────
// Per-user preview prefs (Option-3 hybrid: localStorage cache + server PUT)
// ──────────────────────────────────────────────────────────────────────────

function clampSharePct(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return PIP_SHARE_DEFAULT;
  return Math.min(PIP_SHARE_MAX, Math.max(PIP_SHARE_MIN, value));
}

function clampFraction(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

function clampPosition(value: unknown): PipPositionPct {
  if (typeof value !== "object" || value === null) return { ...DEFAULT_PREVIEW_PREFS.pipPosition };
  const o = value as { leftPct?: unknown; topPct?: unknown };
  return {
    leftPct: clampFraction(o.leftPct),
    topPct: clampFraction(o.topPct),
  };
}

export function normalizePreviewPrefs(raw: unknown): PreviewPrefs {
  const o = (raw && typeof raw === "object" ? raw : {}) as {
    pipSharePct?: unknown;
    pipPosition?: unknown;
  };
  return {
    pipSharePct: clampSharePct(o.pipSharePct),
    pipPosition: clampPosition(o.pipPosition),
  };
}

function cacheKey(userId: string): string {
  return `${PREVIEW_PREFS_CACHE_PREFIX}${userId}`;
}

function lsGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function lsSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function lsRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * Read the cached preview prefs for `userId`. Returns the documented
 * defaults when no cache exists or the cache is corrupt — callers should
 * still kick off a server hydration in parallel.
 */
export function getCachedPreviewPrefs(userId: string | null | undefined): PreviewPrefs {
  if (!userId) {
    return {
      ...DEFAULT_PREVIEW_PREFS,
      pipPosition: { ...DEFAULT_PREVIEW_PREFS.pipPosition },
    };
  }
  const raw = lsGet(cacheKey(userId));
  if (!raw) {
    return {
      ...DEFAULT_PREVIEW_PREFS,
      pipPosition: { ...DEFAULT_PREVIEW_PREFS.pipPosition },
    };
  }
  try {
    return normalizePreviewPrefs(JSON.parse(raw));
  } catch {
    return {
      ...DEFAULT_PREVIEW_PREFS,
      pipPosition: { ...DEFAULT_PREVIEW_PREFS.pipPosition },
    };
  }
}

/**
 * Merge `prefs` into the cached row for `userId` (partial updates allowed),
 * then persist normalized values.
 */
export function setCachedPreviewPrefs(
  userId: string | null | undefined,
  prefs: Partial<PreviewPrefs>,
): void {
  if (!userId) return;
  const prev = getCachedPreviewPrefs(userId);
  const merged: PreviewPrefs = {
    pipSharePct: prefs.pipSharePct ?? prev.pipSharePct,
    pipPosition: {
      leftPct: prefs.pipPosition?.leftPct ?? prev.pipPosition.leftPct,
      topPct: prefs.pipPosition?.topPct ?? prev.pipPosition.topPct,
    },
  };
  lsSet(cacheKey(userId), JSON.stringify(normalizePreviewPrefs(merged)));
}

/**
 * Wipe the cached preview prefs. Called on logout / auth-expired so the
 * next user on the same browser doesn't inherit the previous one's PiP
 * geometry. When `userId` is provided, only that user's cache is removed;
 * pass `null` to sweep every cached user (defense-in-depth on logout).
 */
export function clearCachedPreviewPrefs(userId: string | null | undefined): void {
  if (userId) {
    lsRemove(cacheKey(userId));
    return;
  }
  try {
    const toDrop: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREVIEW_PREFS_CACHE_PREFIX)) toDrop.push(k);
    }
    for (const k of toDrop) lsRemove(k);
  } catch {
    /* ignore */
  }
}
